import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Toast, ContactsSelect, LoadingOverlay, ModalProps, ModalActions, Modal } from '@pletly/ui';

import { getName } from '@pletly/shared';
import { useAuthHeaders } from '@pletly/feature-auth';

import { useOrganisationData } from '../useOrganisationData';
import { useAssignCareReceiverMemberToCaregiverMembersMutation } from '../__generated__/assignCaregivers.mutation.generated';
import { withTranslation } from '../../../../locales/hoc';

interface CareRecipientMember {
	memberId: string;
	uuid: string;
	username: string;
	firstName?: string | null;
	lastName?: string | null;
	superPals: {
		id: string;
	}[];
}

type Props<CareRecipientMemberInfo extends CareRecipientMember> = {
	organisationUuid: string;
	careRecipientMember: CareRecipientMemberInfo;
} & Omit<ModalProps, 'children'>;

export const AssignModal = <CareRecipientMemberInfo extends CareRecipientMember>({
	organisationUuid,
	careRecipientMember,
	onClose,
	...modalProps
}: Props<CareRecipientMemberInfo>) => {
	const { t } = useTranslation();
	const { getHeaders } = useAuthHeaders();
	const { loading, organisation } = useOrganisationData({ uuid: organisationUuid });
	const [assignCaregivers, { loading: assignMutationLoading }] =
		useAssignCareReceiverMemberToCaregiverMembersMutation();

	const initialCaregiverMemberIds = useMemo(() => {
		if (!organisation || !careRecipientMember.superPals.length) return [];

		const superPalsUserIds = careRecipientMember.superPals.map((superPal) => superPal.id);

		return organisation.members.caregivers.reduce<string[]>((ids, caregiver) => {
			if (superPalsUserIds.includes(caregiver.id)) {
				ids.push(caregiver.memberId);
			}

			return ids;
		}, []);
	}, [organisation, careRecipientMember.superPals]);

	const [selectedIds, setSelectedIds] = useState<string[]>(initialCaregiverMemberIds);
	const handleSubmit = async () => {
		if (!selectedIds.length && !initialCaregiverMemberIds.length) {
			return onClose?.();
		}

		try {
			await assignCaregivers({
				variables: {
					data: {
						organisationUuid,
						caregiverMembersIds: selectedIds,
						careRecipientMemberId: careRecipientMember.memberId,
					},
				},
			});
			Toast.show({
				type: 'success',
				text1: t('toasts.success.caregivers-assigned'),
			});
		} catch {
			Toast.show({
				type: 'error',
				text1: t('toasts.error.something-wrong'),
			});
		} finally {
			onClose?.();
		}
	};

	const contacts = useMemo(() => {
		return organisation?.members.caregivers.map(({ memberId, photoUrl, ...caregiver }) => ({
			id: memberId,
			name: getName(caregiver),
			photoSource: photoUrl ? { uri: photoUrl, headers: getHeaders(photoUrl) } : undefined,
		}));
	}, [organisation?.members.caregivers, getHeaders]);

	const name = getName(careRecipientMember);

	if (loading) {
		return <LoadingOverlay show />;
	}

	if (!organisation) {
		return null;
	}

	return (
		<Modal
			{...modalProps}
			title={t('assign-caregivers-to', { name })}
			onClose={onClose}
			withOwnToast={false}
			actions={
				<ModalActions
					primaryAction={{
						label: t('assign'),
						onPress: handleSubmit,
						disabled: assignMutationLoading || loading,
					}}
					secondaryAction={{
						label: t('common.cancel'),
						onPress: onClose,
					}}
				/>
			}
		>
			<ContactsSelect
				mode="multi"
				contacts={contacts || []}
				selectedIds={selectedIds}
				setSelectedIds={setSelectedIds}
			/>
		</Modal>
	);
};

export const AssignCaregiverModal = withTranslation(AssignModal);
